/**
 * Canonical URL helpers for the two post types that have readable URLs:
 * Itinerary (`/itinerary/<slug>-<short_id>`) and List (`/list/<slug>-<short_id>`).
 *
 * The backend mirror lives at AppBackend/utils/post_url_helpers.js; keep the
 * slug rules identical so both sides emit the same string for the same post.
 * The GTA-601 Notion ticket (short name + 6-hex suffix) sets the format.
 */

export const SLUG_MAX_LENGTH = 60;
export const SLUG_FALLBACK = 'untitled';

const DEFAULT_POST_NAMES = new Set(['my itinerary', 'my list']);

export function slugifyPostName(name: string | null | undefined): string {
  if (typeof name !== 'string') return SLUG_FALLBACK;
  const trimmed = name.trim();
  if (!trimmed) return SLUG_FALLBACK;
  if (DEFAULT_POST_NAMES.has(trimmed.toLowerCase())) return SLUG_FALLBACK;

  const slug = trimmed
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, '');

  return slug || SLUG_FALLBACK;
}

export const POST_TYPE_SEGMENT = {
  Itinerary: 'itinerary',
  List: 'list',
} as const;

export type ReadableUrlPostType = keyof typeof POST_TYPE_SEGMENT;

export interface CanonicalPostInput {
  _id?: string | null;
  post_type?: string | null;
  name?: string | null;
  short_id?: string | null;
}

export function isReadableUrlPostType(postType: unknown): postType is ReadableUrlPostType {
  return typeof postType === 'string' && Object.prototype.hasOwnProperty.call(POST_TYPE_SEGMENT, postType);
}

const BARE_OBJECT_ID = /^[a-f0-9]{24}$/i;
const SHORT_ID_IN_PARAM = /(?:^|-)([a-f0-9]{6,23})$/i;

export function isObjectIdHex(value: unknown): value is string {
  return typeof value === 'string' && BARE_OBJECT_ID.test(value);
}

/** True when the route param is this post's ObjectId or its readable short-id URL. */
export function routeParamMatchesPost(
  routeParam: string | null | undefined,
  post: { _id?: string | null; short_id?: string | null } | null | undefined,
): boolean {
  if (!post?._id || typeof routeParam !== 'string') return false;
  const param = routeParam.trim();
  if (!param) return false;
  if (param === String(post._id)) return true;
  const shortId = typeof post.short_id === 'string' ? post.short_id.trim().toLowerCase() : '';
  if (!shortId) return false;
  return extractShortIdFromParam(param) === shortId;
}

/**
 * Pull the short_id suffix out of a URL segment such as `japan-trip-6854ca`
 * or the bare `6854ca` form. Returns null for 24-hex ObjectIds (callers
 * dispatch those separately) and for anything without a 6..23 char trailing
 * hex token.
 */
export function extractShortIdFromParam(param: string | null | undefined): string | null {
  if (typeof param !== 'string') return null;
  const trimmed = param.trim();
  if (!trimmed || isObjectIdHex(trimmed)) return null;
  const match = trimmed.match(SHORT_ID_IN_PARAM);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Build the canonical path for an Itinerary or List post. Falls back to the
 * legacy `/<segment>/<_id>` form when the post has no short_id yet, so
 * pre-backfill posts still resolve. Returns null when the post is not a
 * readable-URL type (other post_types continue to live at `/post/<_id>`).
 */
export function buildPostCanonicalPath(post: CanonicalPostInput | null | undefined): string | null {
  if (!post || !isReadableUrlPostType(post.post_type)) return null;
  const segment = POST_TYPE_SEGMENT[post.post_type];
  const idStr = post._id ?? null;
  const shortId = typeof post.short_id === 'string' && post.short_id ? post.short_id : null;
  if (!shortId) return idStr ? `/${segment}/${idStr}` : null;
  const slug = slugifyPostName(post.name);
  return `/${segment}/${slug}-${shortId}`;
}

/**
 * Convenience for feed/list card click handlers that only carry id + title + postType
 * (and now short_id after GTA-601). Returns the readable URL for Itinerary/List when
 * short_id is present, the ObjectId fallback otherwise, and `/place/<id>` for Places.
 */
export function routeForPostCard(input: {
  id: string;
  postType?: string | null;
  title?: string | null;
  shortId?: string | null;
}): string {
  const canonical = buildPostCanonicalPath({
    _id: input.id,
    post_type: input.postType ?? null,
    name: input.title ?? null,
    short_id: input.shortId ?? null,
  });
  if (canonical) return canonical;
  if (input.postType === 'Place') return `/place/${input.id}`;
  if (input.postType === 'List') return `/list/${input.id}`;
  if (input.postType === 'Itinerary') return `/itinerary/${input.id}`;
  return `/post/${input.id}`;
}
